import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Drawer,
  Empty,
  Input,
  List,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  message as antdMessage,
} from "antd";

import {
  listFacilityAiFeedbackForReview,
  reviewFacilityAiFeedback,
} from "./api";
import { canApproveForFineTuning } from "./feedback.mjs";


const STATUS_OPTIONS = [
  ["", "All statuses"],
  ["new", "New"],
  ["triaged", "Triaged"],
  ["needs_correction", "Needs correction"],
  ["eligible", "Eligible"],
  ["approved", "Approved for fine-tuning"],
  ["rejected", "Rejected"],
];

const ROOT_CAUSE_OPTIONS = [
  ["model", "Model behavior"],
  ["data", "Data quality or freshness"],
  ["retrieval", "Retrieval or evidence"],
  ["permission", "Authorization or scope"],
  ["ui", "Product or interface"],
  ["safety", "Safety or privacy"],
  ["other", "Other"],
];

const shortText = (value, length = 220) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  return text.length > length ? `${text.slice(0, length)}…` : text;
};


const FeedbackReviewDrawer = ({ visible, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("new");
  const [selected, setSelected] = useState(null);
  const [rootCause, setRootCause] = useState("model");
  const [trainingUse, setTrainingUse] = useState("sft");
  const [correctedAnswer, setCorrectedAnswer] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!visible) return;
    setLoading(true);
    try {
      const result = await listFacilityAiFeedbackForReview({
        status: statusFilter,
        pageSize: 100,
      });
      setItems(Array.isArray(result?.results) ? result.results : []);
    } catch (error) {
      antdMessage.error(error?.message || "Unable to load the feedback review queue.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, visible]);

  useEffect(() => {
    load();
  }, [load]);

  const openReview = (item) => {
    setSelected(item);
    setRootCause(item.root_cause || "model");
    setTrainingUse(["sft", "preference"].includes(item.training_use) ? item.training_use : "sft");
    setCorrectedAnswer(item.corrected_answer || item.suggested_answer || item.answer_snapshot || "");
    setReviewNote(item.review_note || "");
  };

  const decide = async (nextStatus, nextTrainingUse) => {
    if (!selected) return;
    setSaving(true);
    try {
      const result = await reviewFacilityAiFeedback(selected.id, {
        status: nextStatus,
        root_cause: rootCause,
        review_note: reviewNote.trim(),
        corrected_answer: correctedAnswer.trim(),
        training_use: nextTrainingUse,
      });
      setItems((current) =>
        statusFilter && result.status !== statusFilter
          ? current.filter((item) => item.id !== result.id)
          : current.map((item) => (item.id === result.id ? result : item))
      );
      setSelected(null);
      antdMessage.success(
        nextStatus === "approved" ? "Feedback approved for fine-tuning." : "Review saved."
      );
    } catch (error) {
      antdMessage.error(error?.message || "Unable to save the review decision.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Drawer
        title="AI feedback review"
        visible={visible}
        onClose={onClose}
        width={760}
        className="facility-ai-feedback-review-drawer"
      >
        <div className="facility-ai-feedback-review-toolbar">
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 220 }}>
            {STATUS_OPTIONS.map(([value, label]) => (
              <Select.Option key={value || "all"} value={value}>{label}</Select.Option>
            ))}
          </Select>
          <Button onClick={load} loading={loading}>Refresh</Button>
          <span>{items.length} feedback items</span>
        </div>
        {loading ? (
          <div className="facility-ai-feedback-review-loading"><Spin /></div>
        ) : items.length ? (
          <List
            dataSource={items}
            renderItem={(item) => (
              <List.Item
                className="facility-ai-feedback-review-row"
                actions={[<Button key="review" onClick={() => openReview(item)}>Review</Button>]}
              >
                <List.Item.Meta
                  title={
                    <Space wrap>
                      <Tag color={item.rating === "helpful" ? "green" : "red"}>
                        {item.rating === "helpful" ? "Helpful" : "Not helpful"}
                      </Tag>
                      <Tag>{item.status.replace(/_/g, " ")}</Tag>
                      {item.training_consent ? <Tag color="blue">Training consent</Tag> : <Tag>No consent</Tag>}
                      <span>{item.facility_name}</span>
                    </Space>
                  }
                  description={
                    <div className="facility-ai-feedback-review-summary">
                      <strong>Question</strong>
                      <span>{shortText(item.question_snapshot)}</span>
                      <strong>Answer</strong>
                      <span>{shortText(item.answer_snapshot)}</span>
                      {item.suggested_answer ? (
                        <><strong>User suggestion</strong><span>{shortText(item.suggested_answer)}</span></>
                      ) : null}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty description="No feedback matches this filter." />
        )}
      </Drawer>

      <Modal
        title="Review AI feedback"
        visible={Boolean(selected)}
        onCancel={() => setSelected(null)}
        width={780}
        footer={null}
        destroyOnClose={false}
      >
        {selected ? (
          <div className="facility-ai-feedback-review-form">
            <div className="facility-ai-feedback-review-context">
              <strong>User question</strong>
              <p>{selected.question_snapshot}</p>
              <strong>Assistant answer</strong>
              <p>{selected.answer_snapshot}</p>
              {selected.comment ? <><strong>User comment</strong><p>{selected.comment}</p></> : null}
              {selected.suggested_answer ? <><strong>User suggestion</strong><p>{selected.suggested_answer}</p></> : null}
            </div>
            <label>Root cause</label>
            <Select value={rootCause} onChange={setRootCause} style={{ width: "100%" }}>
              {ROOT_CAUSE_OPTIONS.map(([value, label]) => (
                <Select.Option key={value} value={value}>{label}</Select.Option>
              ))}
            </Select>
            <label>Reviewed target answer</label>
            <Input.TextArea
              value={correctedAnswer}
              onChange={(event) => setCorrectedAnswer(event.target.value)}
              rows={7}
              maxLength={20000}
            />
            <label>Review note</label>
            <Input.TextArea
              value={reviewNote}
              onChange={(event) => setReviewNote(event.target.value)}
              rows={3}
              maxLength={8000}
            />
            <label>Fine-tune format</label>
            <Select value={trainingUse} onChange={setTrainingUse} style={{ width: "100%" }}>
              <Select.Option value="sft">Supervised fine-tuning (SFT)</Select.Option>
              <Select.Option value="preference">Preference tuning</Select.Option>
            </Select>
            {!selected.training_consent ? (
              <div className="facility-ai-feedback-consent-warning">
                The user did not consent to model training. Approval is disabled; this item can still be triaged, rejected, or kept for evaluation.
              </div>
            ) : null}
            <Space wrap>
              <Button onClick={() => decide("triaged", "none")} loading={saving}>Save triage</Button>
              <Button onClick={() => decide("eligible", "eval")} loading={saving}>Evaluation only</Button>
              <Button danger onClick={() => decide("rejected", "none")} loading={saving}>Reject</Button>
              <Button
                type="primary"
                onClick={() => decide("approved", trainingUse)}
                loading={saving}
                disabled={!canApproveForFineTuning({
                  trainingConsent: selected.training_consent,
                  rootCause,
                  correctedAnswer,
                })}
              >
                Approve for fine-tuning
              </Button>
            </Space>
          </div>
        ) : null}
      </Modal>
    </>
  );
};


export default FeedbackReviewDrawer;
